import { graphql, useStaticQuery } from 'gatsby';
import { useEffect, useState } from 'react';

interface ChecklistItem {
  id: string;
  documentId: string;
  contentfulName: string;
  primaryText: string;
  isCompleted: boolean;
}

interface PageInfo {
  contentfulName: string;
  slug: string;
}

export interface ChecklistNode {
  layout: 'SimpleChecklist' | 'ComplexChecklist';
  id: string;
  documentId: string;
  contentfulName: string;
  primaryText: string;
  page: PageInfo[] | null;
  checklistItems: ChecklistItem[];
  isCompleted: boolean;
  celebratoryStatusAchieved: boolean;
}

// Get all checklist entries from the CMS to be used in the checklist context
// Filter to include both simple and complex checklists
// Filter to include only English language content otherwise we get duplicates for the empty FR entries
// Filter to include only checklists that are associated with a page (regex)
// Filter to exclude placeholder content
export const useChecklistCmsData = (): ChecklistNode[] => {
  const response = useStaticQuery(graphql`
    query {
      checklists: allStrapiPageSection(
        filter: { layout: { in: ["SimpleChecklist", "ComplexChecklist"] } }
        sort: { updatedAt: DESC }
      ) {
        nodes {
          layout
          id
          documentId
          contentfulName
          primaryText
          checklistItems {
            id
            documentId
            contentfulName
            primaryText
          }
        }
      }
    }
  `);

  const [checklistResults, setChecklistResults] = useState<ChecklistNode[]>([]);

  useEffect(() => {
    const fetchChecklistPageData = async ({
      documentId,
      ...rest
    }: {
      documentId: string;
      [key: string]: string;
    }) => {
      try {
        const response = await fetch(
          `${process.env.STRAPI_URL_ADC}/api/parent-references/${documentId}/pages-grouped`,
          {
            headers: {
              Authorization: `Bearer ${process.env.STRAPI_TOKEN_ADC}`,
            },
          },
        );
        const data = await response.json();

        // Helper function to process dynamic page with URL params
        const processDynamicPage = (dynamicPage: any) => {
          // add URL params to dynamic page slugs to trigger the correct drop down value/URLparams, for use in links associated with the checklists from a dynamic page (currently in profile blade)
          const searchParams = new URLSearchParams();
          searchParams.set('content', dynamicPage.sectionTitle);
          return {
            ...dynamicPage,
            slug: `${dynamicPage.slug}?${searchParams}`,
          };
        };

        // Get first page or first dynamic page after processing
        const page =
          data.pages && data.pages.length > 0
            ? data.pages
            : data.dynamicPages && data.dynamicPages.length > 0
              ? data.dynamicPages?.map(processDynamicPage)
              : [];

        return { ...rest, page };
      } catch (err) {
        console.log('Error occurred while fetching parent pages - ', err);
        return { ...rest, error: err };
      }
    };

    const fetchAllChecklists = async () => {
      const promises = response.checklists.nodes.map(fetchChecklistPageData);
      const results = await Promise.all(promises);
      
      // Process checklistResults and merge with GraphQL nodes
      const processedChecklists = response.checklists.nodes
        .map((node: any, index: number) => {
          const apiResult = results[index];

          // If no API result or error, return node without page data
          if (!apiResult || apiResult.error) {
            return {
              ...node,
              page: null,
              checklistItems: node.checklistItems || [],
            };
          }

          // Merge the page data from API with the GraphQL node
          return {
            ...node,
            page: apiResult.page,
            checklistItems: node.checklistItems || [],
          };
        })
        .filter((checklist: ChecklistNode) => {
          // Filter out checklists where page is an empty array
          return !Array.isArray(checklist.page) || checklist.page.length > 0;
        });
      
      setChecklistResults(processedChecklists);
    };

    fetchAllChecklists();
  }, [response.checklists.nodes]);

  return checklistResults;
};
